/**
 * Shopify Provider wrapper for TanStack Start
 * Uses react-i18next instead of next-intl
 */
import { CartProvider, ShopifyProvider } from "@shopify/hydrogen-react";
import { useTranslation } from "react-i18next";
import { countries } from "@modules/shared/localization/const/countries";
import { storeFrontApiVersion } from "./shopify";
import { ShopifyAnalyticsTracker } from "./analytics/ShopifyAnalyticsTracker";
import { ConsentProvider } from "./consent/ConsentProvider";
import type { PropsWithChildren } from "react";

const cartCustomFragment = `
fragment CartFragment on Cart {
  id
  checkoutUrl
  totalQuantity
  buyerIdentity {
    countryCode
    email
    phone
    customer {
      id
      email
      firstName
      lastName
      displayName
      defaultAddress {
        firstName
        lastName
        phone
        zip
        address1
        address2
        city
        country
        countryCodeV2
      }
    }
  }
  lines(first: $numCartLines) {
    edges {
      node {
        id
        quantity
        attributes {
          key
          value
        }
        discountAllocations {
          discountedAmount {
            ...MoneyFragment
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
        }
        merchandise {
          ... on ProductVariant {
            id
            availableForSale
            compareAtPrice {
              ...MoneyFragment
            }
            price {
              ...MoneyFragment
            }
            requiresShipping
            title
            image {
              ...ImageFragment
            }
            product {
              handle
              title
              id
              productType
            }
            selectedOptions {
              name
              value
            }
          }
        }
      }
    }
  }
  cost {
    subtotalAmount {
      ...MoneyFragment
    }
    totalAmount {
      ...MoneyFragment
    }
    checkoutChargeAmount {
      ...MoneyFragment
    }
  }
  note
  attributes {
    key
    value
  }
  discountAllocations {
    discountedAmount {
      ...MoneyFragment
    }
  }
  discountCodes {
    code
  }
}
fragment MoneyFragment on MoneyV2 {
  currencyCode
  amount
}
fragment ImageFragment on Image {
  id
  url
  altText
  width
  height
}
`;

interface ShopProviderProps extends PropsWithChildren {
  customerAccessToken?: string | null;
}

export const ShopProvider: React.FC<ShopProviderProps> = ({
  children,
  customerAccessToken,
}) => {
  const { i18n } = useTranslation();
  const locale = i18n.language as keyof typeof countries;
  const countryConfig = countries[locale];

  return (
    <ShopifyProvider
      storefrontId="hono-pono-shop"
      countryIsoCode={countryConfig.country}
      languageIsoCode={countryConfig.language}
      storeDomain={import.meta.env.VITE_SHOPIFY_STORE_URL}
      storefrontApiVersion={storeFrontApiVersion}
      storefrontToken={import.meta.env.VITE_STOREFRONT_API_PUBLIC_ACCESS_TOKEN}
      // Cart requests go through src/routes/api/$version/graphql[.]json.tsx so
      // Shopify can set its tracking cookies on our domain.
      sameDomainForStorefrontApi
    >
      <CartProvider
        customerAccessToken={customerAccessToken ?? null}
        onCreate={() => {
          // Fire event for cart popup
          dispatchEvent(new Event("cart:lineItemAdded"));
        }}
        onLineAddComplete={() => {
          dispatchEvent(new Event("cart:lineItemAdded"));
        }}
        cartFragment={cartCustomFragment}
      >
        <ConsentProvider
          checkoutDomain={
            import.meta.env.VITE_CHECKOUT_DOMAIN ??
            import.meta.env.VITE_SHOPIFY_STORE_URL
          }
          storefrontRootDomain={import.meta.env.VITE_STOREFRONT_ROOT_DOMAIN}
          storefrontAccessToken={
            import.meta.env.VITE_STOREFRONT_API_PUBLIC_ACCESS_TOKEN
          }
          locale={countryConfig.language}
          country={countryConfig.country}
        >
          <ShopifyAnalyticsTracker />
          {children}
        </ConsentProvider>
      </CartProvider>
    </ShopifyProvider>
  );
};
